<template>
  <div class="modal-backdrop">
    <div class="popup-content history-modal">
      <button class="history-modal__close" type="button" @click="$emit('close')">
        ✕
      </button>

      <div class="battery-history-card">
        <div class="battery-history-header">
          <div class="battery-title-wrap">
            <div class="battery-icon">
              <BatteryIndicator :level="batteryPercent" />
            </div>
            <h2 class="battery-title">Батарейка</h2>
          </div>
        </div>

        <div class="history-panel">
          <div class="history-panel-header">
            <h3 class="panel-title">История изменений</h3>

            <div class="filter-block">
              <label for="periodFilter" class="filter-label">Период</label>
              <select id="periodFilter" v-model="selectedPeriod" class="filter-select">
                <option value="day">День</option>
                <option value="week">Неделя</option>
                <option value="month">Месяц</option>
                <option value="all">Все</option>
              </select>
            </div>
          </div>

          <table class="history-table">
            <thead>
            <tr>
              <th>Дата / время</th>
              <th>Действие</th>
              <th>Эмоции</th>
              <th>Индикатор</th>
            </tr>
            </thead>

            <tbody>
            <tr v-for="item in tableItems" :key="item.id">
              <td>{{ item.date }}</td>
              <td>{{ item.action }}</td>
              <td>
                <div
                    v-if="item.emotion"
                    class="emotion-square"
                    :style="{ backgroundColor: emotionColors[item.emotion] || '#ccc' }"
                />
              </td>
              <td>
                <div class="remaining-amount">
                  <div class="amount-indicator">
                    {{ item.indicator }}
                  </div>
                  <div>
                    <battery-indicator :level="item.remainingAmount" :size="'small'" />
                  </div>
                </div>
              </td>
            </tr>

            <tr v-if="!tableItems.length">
              <td colspan="4" class="empty-row">Нет данных</td>
            </tr>
            </tbody>
          </table>

          <div class="pagination" v-if="lastPage > 1">
            <button
                class="page-btn"
                type="button"
                :disabled="currentPage <= 1"
                @click="goToPrevPage"
            >
              ←
            </button>

            <span class="page-info">
              {{ currentPage }} / {{ lastPage }}
            </span>

            <button
                class="page-btn"
                type="button"
                :disabled="currentPage >= lastPage"
                @click="goToNextPage"
            >
              →
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onBeforeMount } from 'vue'
import { useStore } from 'vuex'
import BatteryIndicator from '@/views/MoodTracker/BatteryIndicator.vue'

export default {
  name: 'BatteryHistoryTable',
  components: { BatteryIndicator },
  emits: ['close'],

  setup() {
    const store = useStore()

    const selectedPeriod = ref('all')
    const currentPage = ref(1)

    const actionTranslateBlock = {
      correct_emotion_and_task: 'Верно сопоставлена эмоция и задача',
      feedback_given: 'Проставлена оценка по задаче',
      fav_strategies_bonus: 'Бонус за 5 добавленных стратегий',
      refresh: 'Сброс до 100%',
    }

    const emotionColors = {
      red: '#fd0a07',
      yellow: '#fde80c',
      blue: '#183695',
      green: '#6dba33',
    }

    const colorsTranslateMap = {
      red: 'Красный',
      yellow: 'Жёлтый',
      blue: 'Синий',
      green: 'Зелёный',
    }

    const transactionsResponse = computed(() => store.state.tracker.batteryTransactions || {})
    const transactions = computed(() => transactionsResponse.value.data || [])
    const meta = computed(() => transactionsResponse.value.meta || {})

    const batteryPercent = computed(() => store.state.tracker.batteryLevel)

    const lastPage = computed(() => meta.value.last_page || 1)

    const tableItems = computed(() => {
      return transactions.value.map((el) => ({
        id: el.id,
        date: el.created_at,
        action: prepareAction(el),
        indicator: prepareIndicator(el),
        indicatorRaw: Number(el.amount),
        emotion: el.color_name ? prepareEmotion(el.color_name) : null,
        remainingAmount: el.remaining_amount,
      }))
    })

    const fetchRecords = () => {
      store.dispatch('tracker/getBatteryTransactions', {
        page: currentPage.value,
        period: selectedPeriod.value,
      })
    }

    const prepareAction = (data) => {
      if (data.event_type === 'switch_emotion_or_task') {
        return `Смена ${data.change_type === 'color' ? 'эмоции' : 'задачи'}`
      }

      return actionTranslateBlock[data.event_type] ?? data.event_type
    }

    const prepareIndicator = (data) => {
      const sign = data.operation_type === 'append' ? '+' : '-'
      return `${sign} ${data.amount}%`
    }

    const prepareEmotion = (colorName) => {
      return Object.keys(colorsTranslateMap).find(
          (key) => colorsTranslateMap[key] === colorName
      );
    }

    const goToPage = (page) => {
      if (page < 1 || page > lastPage.value || page === currentPage.value) {
        return
      }

      currentPage.value = page
      fetchRecords();
    }

    const goToPrevPage = () => {
      goToPage(currentPage.value - 1);
    }

    const goToNextPage = () => {
      goToPage(currentPage.value + 1);
    }

    watch(selectedPeriod, () => {
      currentPage.value = 1;
      fetchRecords();
    })

    onBeforeMount(() => {
      fetchRecords();
    })

    return {
      selectedPeriod,
      currentPage,
      lastPage,
      batteryPercent,
      emotionColors,
      tableItems,
      goToPrevPage,
      goToNextPage,
    }
  },
}
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.7);
  overflow-y: auto;
  display: flex;
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;
}

.history-modal {
  position: relative;
  width: 900px;
  max-width: 100%;
  background: #ffffff;
  border-radius: 26px;
  padding: 26px 34px 28px;
  box-sizing: border-box;
  font-family: Arial, sans-serif;
  margin: auto 0;
}

.history-modal__close {
  position: absolute;
  top: 18px;
  right: 18px;
  border: none;
  background: transparent;
  font-size: 28px;
  line-height: 1;
  color: #8f8f8f;
  cursor: pointer;
  padding: 0;
}

.battery-history-card {
  max-width: 860px;
  overflow-x: auto;
}

.battery-history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.battery-title-wrap {
  display: flex;
  align-items: center;
  gap: 12px;
}

.battery-title {
  margin: 0;
  font-size: 34px;
  font-weight: 700;
  color: #1f1f1f;
}

.filter-block {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-label {
  font-size: 14px;
  color: #555;
}

.filter-select {
  border: 1px solid #ececec;
  background: white;
  border-radius: 12px;
  padding: 8px 12px;
  font-size: 14px;
  outline: none;
}

.panel-title {
  font-size: 26px;
  font-weight: 700;
  color: #1f1f1f;
}

.history-table {
  width: 100%;
  border-collapse: collapse;
  background: transparent;
}

.history-table th,
.history-table td {
  text-align: left;
  padding: 12px 10px;
  border-bottom: 1px solid #c9c9c9;
  vertical-align: middle;
  font-size: 14px;
  color: #222;
}

.history-table tr:nth-child(even) {
  background: #f8f8f8;
}

.history-table th {
  font-weight: 700;
}

.emotion-square {
  width: 32px;
  height: 32px;
  border-radius: 2px;
}

.indicator-positive {
  color: #1f9d45;
  font-weight: 600;
}

.indicator-negative {
  color: #d14343;
  font-weight: 600;
}

.indicator-neutral {
  color: #666;
  font-weight: 600;
}

.empty-row {
  text-align: center;
  color: #888;
  padding: 24px 0;
}

.pagination {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 18px;
  justify-content: flex-end;
}

.page-btn {
  border: none;
  background: white;
  border-radius: 10px;
  min-width: 34px;
  height: 34px;
  padding: 0 10px;
  cursor: pointer;
  font-size: 14px;
}

.page-btn:disabled {
  opacity: 0.4;
  cursor: default;
}

.page-info {
  font-size: 14px;
  color: #555;
}

.history-panel-header {
  margin: 8px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
}

@media (max-width: 900px) {
  .history-panel-header {
    align-items: flex-start;
    flex-direction: column;
  }
}

.remaining-amount {
  display: flex;
  gap: 6px;
  align-items: center;

  .amount-indicator {
    width: 50px;
    text-align: right;
  }
}
</style>